import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus, Search, Filter, Calendar, Clock, Edit, Trash2, Copy,
  ChevronLeft, ChevronRight, Eye, Play,
  Youtube, Instagram, Twitter, Linkedin, Facebook, FileText,
  CheckCircle, AlertCircle, Clock3, XCircle, Users,
  MoreHorizontal, CalendarDays, CalendarRange, CalendarClock,

} from 'lucide-react';

import AddContentModal from './AddContentModal';
import { useCalendarStore, ContentItem, PlatformName, ScheduleStatus } from '../../store/calendarStore';

interface DragState {
  isDragging: boolean;
  draggedItem: ContentItem | null;
  draggedOver: string | null;
}

const ContentCalendarPage: React.FC = () => {
  const {
    contentItems,
    selectedContent,
    currentDate,
    viewMode,
    isLoading,
    searchTerm,
    filters,
    selectedItems,
    setCurrentDate,
    setViewMode,
    setSearchTerm,
    setFilters,
    setSelectedContent,
    fetchContentItems,
    deleteContent,
    duplicateContent,
    publishContent,
    toggleItemSelection,
    clearSelection,
    getContentForDate,
    getContentStats,
    rescheduleContent,
    updateContent,
    // exportContentItems
  } = useCalendarStore();

  const [showFilters, setShowFilters] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedItem: null,
    draggedOver: null
  });
  // const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    fetchContentItems(startDate, endDate);
  }, [currentDate, fetchContentItems]);

  const stats = useMemo(() => getContentStats(), [contentItems]);

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, content: ContentItem) => {
    setDragState({
      isDragging: true,
      draggedItem: content,
      draggedOver: null
    });

    // Set drag effect
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', content.id);

    // Add dragging class to the element
    const target = e.currentTarget as HTMLElement;
    target.classList.add('opacity-50');
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDragState({
      isDragging: false,
      draggedItem: null,
      draggedOver: null
    });

    // Remove dragging class
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('opacity-50');
  };

  const handleDragOver = (e: React.DragEvent, date: Date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const dateKey = date.toDateString();
    if (dragState.draggedOver !== dateKey) {
      setDragState(prev => ({
        ...prev,
        draggedOver: dateKey
      }));
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear drag over if we're leaving the drop zone entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragState(prev => ({
        ...prev,
        draggedOver: null
      }));
    }
  };

  const handleDrop = async (e: React.DragEvent, targetDate: Date) => {
    e.preventDefault();

    const contentId = e.dataTransfer.getData('text/plain');
    const draggedContent = dragState.draggedItem;

    if (draggedContent && contentId === draggedContent.id) {
      // Check if we're dropping on a different date
      const originalDate = new Date(draggedContent.scheduledDate);
      const isSameDate = targetDate.toDateString() === originalDate.toDateString();

      if (!isSameDate) {
        try {
          await rescheduleContent(
            draggedContent.id,
            targetDate,
            draggedContent.scheduledTime
          );

          // Show success feedback
          console.log(`Content "${draggedContent.title}" moved to ${targetDate.toDateString()}`);
        } catch (error) {
          console.error('Failed to reschedule content:', error);
        }
      }
    }

    setDragState({
      isDragging: false,
      draggedItem: null,
      draggedOver: null
    });
  };

  const getPlatformIcon = (platform: PlatformName) => {
    const icons = {
      youtube: <Youtube className="w-4 h-4" />,
      instagram: <Instagram className="w-4 h-4" />,
      twitter: <Twitter className="w-4 h-4" />,
      linkedin: <Linkedin className="w-4 h-4" />,
      facebook: <Facebook className="w-4 h-4" />,
      tiktok: <div className="w-4 h-4 bg-black rounded-sm flex items-center justify-center text-white text-xs font-bold">T</div>,
      blog: <FileText className="w-4 h-4" />
    };
    return icons[platform] || <FileText className="w-4 h-4" />;
  };

  const getPlatformColor = (platform: PlatformName) => {
    const colors = {
      youtube: 'from-red-500 to-red-600',
      instagram: 'from-pink-500 to-purple-600',
      twitter: 'from-primary-400 to-primary-600',
      linkedin: 'from-primary-600 to-primary-700',
      facebook: 'from-primary-600 to-indigo-600',
      tiktok: 'from-gray-800 to-black',
      blog: 'from-green-500 to-green-600'
    };
    return colors[platform] || 'from-gray-500 to-gray-600';
  };

  const getStatusIcon = (status: ScheduleStatus) => {
    const icons = {
      draft: <Edit className="w-4 h-4 text-gray-500" />,
      scheduled: <Calendar className="w-4 h-4 text-primary-500" />,
      published: <CheckCircle className="w-4 h-4 text-green-500" />,
      ready: <CheckCircle className="w-4 h-4 text-emerald-500" />,
      overdue: <AlertCircle className="w-4 h-4 text-red-500" />,
      planning: <Clock3 className="w-4 h-4 text-yellow-500" />,
      failed: <XCircle className="w-4 h-4 text-red-600" />,
      cancelled: <XCircle className="w-4 h-4 text-gray-400 dark:text-gray-500" />
    };
    return icons[status] || <Clock className="w-4 h-4 text-gray-400 dark:text-gray-500" />;
  };

  const getStatusColor = (status: ScheduleStatus) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      scheduled: 'bg-primary-100 text-primary-700',
      published: 'bg-green-100 text-green-700',
      ready: 'bg-emerald-100 text-emerald-700',
      overdue: 'bg-red-100 text-red-700',
      planning: 'bg-yellow-100 text-yellow-700',
      failed: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
    };
    return colors[status] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
  };

  const navigate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(currentDate.getMonth() + (direction === 'next' ? 1 : -1));
    } else if (viewMode === 'week') {
      newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7));
    } else if (viewMode === 'day') {
      newDate.setDate(currentDate.getDate() + (direction === 'next' ? 1 : -1));
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getDaysInWeek = () => {
    const startOfWeek = new Date(currentDate);
    const dayOfWeek = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Monday as first day
    startOfWeek.setDate(diff);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const renderHourRow = (hour: number, date: Date) => {
    const timeStr = `${hour.toString().padStart(2, '0')}:00`;
    const hourContent = getContentForDate(date).filter(item => {
      if (!item.scheduledTime) return false;
      const [itemHour] = item.scheduledTime.split(':').map(Number);
      return itemHour === hour;
    }).slice(0, 2); // Show up to 2 per hour

    return (
      <div key={hour} className="grid grid-cols-24 h-16 border-b border-gray-200 dark:border-gray-700">
        <div className="col-span-1 p-2 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-600 dark:text-gray-400">{timeStr}</div>
        </div>
        <div className="col-span-23 p-2">
          <div className="space-y-1">
            {hourContent.map(content => (
              <div
                key={content.id}
                draggable
                onDragStart={(e) => handleDragStart(e, content)}
                onDragEnd={handleDragEnd}
                className={`text-xs p-1 rounded truncate cursor-move hover:shadow-sm transition-shadow ${getStatusColor(content.status)}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleContentClick(content);
                }}
                title={`${content.title} - Drag to reschedule`}
              >
                <div className="flex items-center gap-1">
                  {getPlatformIcon(content.platform)}
                  <span className="truncate">{content.title}</span>
                </div>
              </div>
            ))}
            {hourContent.length > 2 && (
              <div className="text-xs text-gray-500 dark:text-gray-400">+{hourContent.length - 2} more</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleContentClick = (content: ContentItem) => {
    setSelectedContent(content);
  };

  const handlePublishNow = async (content: ContentItem) => {
    try {
      await publishContent(content.id);
    } catch (error) {
      console.error('Failed to publish content:', error);
    }
  };

  const handleDuplicate = async (content: ContentItem) => {
    try {
      await duplicateContent(content.id);
    } catch (error) {
      console.error('Failed to duplicate content:', error);
    }
  };

  const handleAddContentSuccess = () => {
    setIsAddModalOpen(false);
    const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    fetchContentItems(startDate, endDate);
  };

  const renderCalendarView = () => {
    if (viewMode === 'month') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <button
                onClick={goToToday}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Today
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('prev')}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('next')}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="grid grid-cols-7 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700 last:border-r-0">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {getDaysInMonth().map((date, index) => (
                <div key={index}>
                  {renderCalendarDay(date)}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-100 dark:bg-green-900/30 rounded"></div>
              <span className="text-gray-600 dark:text-gray-400">Published</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-emerald-100 dark:bg-emerald-900/30 rounded"></div>
              <span className="text-gray-600 dark:text-gray-400">Ready</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-primary-100 dark:bg-primary-900/30 rounded"></div>
              <span className="text-gray-600 dark:text-gray-400">Scheduled</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-gray-100 dark:bg-gray-700 rounded"></div>
              <span className="text-gray-600 dark:text-gray-400">Draft</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-100 dark:bg-red-900/30 rounded"></div>
              <span className="text-gray-600 dark:text-gray-400">Overdue</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-yellow-100 dark:bg-yellow-900/30 rounded"></div>
              <span className="text-gray-600 dark:text-gray-400">Planning</span>
            </div>
          </div>
        </div>
      );
    } else if (viewMode === 'week') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Week of {currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </h2>
              <button
                onClick={goToToday}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                This Week
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('prev')}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('next')}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="grid grid-cols-7 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div key={day} className="p-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700 last:border-r-0">
                  {day}<br />
                  <span className="text-xs text-gray-500">{currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {getDaysInWeek().map((date, index) => (
                <div key={index} className="h-64 p-2 border-r border-b border-gray-200 dark:border-gray-700 last:border-r-0">
                  {renderCalendarDay(date)}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    } else if (viewMode === 'day') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </h2>
              <button
                onClick={goToToday}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Today
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('prev')}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('next')}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
            <div className="grid grid-cols-24 font-medium text-sm bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <div className="col-span-1 p-2 text-center border-r border-gray-200 dark:border-gray-700">Time</div>
              <div className="col-span-23 p-2 text-center">Content</div>
            </div>
            {Array.from({ length: 24 }, (_, i) => i).map(hour => renderHourRow(hour, currentDate))}
          </div>
        </div>
      );
    }
    return null;
  };

  const renderCalendarDay = (date: Date | null) => {
    if (!date) {
      return <div className="h-32 p-2"></div>;
    }

    const dayContent = getContentForDate(date);
    const isToday = date.toDateString() === new Date().toDateString();
    const isSelected = selectedDate?.toDateString() === date.toDateString();
    const isDraggedOver = dragState.draggedOver === date.toDateString();

    return (
      <div
        className={`h-32 p-2 border-r border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${isToday ? 'bg-primary-50 dark:bg-primary-900/20' : ''
          } ${isSelected ? 'ring-2 ring-primary-500' : ''} ${isDraggedOver ? 'ring-2 ring-green-500 bg-green-50 dark:bg-green-900/20' : ''
          }`}
        onClick={() => setSelectedDate(date)}
        onDragOver={(e) => handleDragOver(e, date)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, date)}
      >
        <div className={`text-sm font-medium mb-1 ${isToday ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-gray-300'
          }`}>
          {date.getDate()}
          {isToday && <span className="ml-1 text-xs">(Today)</span>}
        </div>

        <div className="space-y-1 overflow-hidden">
          {dayContent.slice(0, 3).map(content => (
            <div
              key={content.id}
              draggable
              onDragStart={(e) => handleDragStart(e, content)}
              onDragEnd={handleDragEnd}
              className={`text-xs p-1 rounded truncate cursor-move hover:shadow-sm transition-shadow ${getStatusColor(content.status)
                }`}
              onClick={(e) => {
                e.stopPropagation();
                handleContentClick(content);
              }}
              title={`${content.title} - Drag to reschedule`}
            >
              <div className="flex items-center gap-1">
                {getPlatformIcon(content.platform)}
                <span className="truncate">{content.title}</span>
              </div>
              {content.scheduledTime && (
                <div className="text-xs opacity-75">{content.scheduledTime}</div>
              )}
            </div>
          ))}
          {dayContent.length > 3 && (
            <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
              +{dayContent.length - 3} more
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContentList = () => {
    const filteredContent = contentItems.filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesPlatform = filters.platforms.length === 0 || filters.platforms.includes(item.platform);
      const matchesStatus = filters.statuses.length === 0 || filters.statuses.includes(item.status);

      return matchesSearch && matchesPlatform && matchesStatus;
    });

    return (
      <div className="space-y-3">
        {filteredContent.map(content => (
          <div
            key={content.id}
            draggable
            onDragStart={(e) => handleDragStart(e, content)}
            onDragEnd={handleDragEnd}
            className={`p-4 border border-gray-100 dark:border-gray-700 rounded-3xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] cursor-move ${selectedItems.includes(content.id) ? 'ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'bg-white dark:bg-gray-800'
              }`}
            onClick={() => handleContentClick(content)}
            title="Drag to calendar to reschedule"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <input
                  type="checkbox"
                  checked={selectedItems.includes(content.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleItemSelection(content.id);
                  }}
                  className="mt-1 h-4 w-4 text-primary-600 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 focus:ring-primary-500"
                />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r text-white ${getPlatformColor(content.platform)}`}>
                      {getPlatformIcon(content.platform)}
                      <span className="ml-1 capitalize">{content.platform}</span>
                    </div>

                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(content.status)}`}>
                      {getStatusIcon(content.status)}
                      <span className="ml-1 capitalize">{content.status}</span>
                    </div>

                    {content.autoPublish && (
                      <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        <Play className="w-3 h-3 mr-1" />
                        Auto
                      </div>
                    )}
                  </div>

                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1 truncate">{content.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">{content.description}</p>

                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {content.scheduledDate.toLocaleDateString()}
                    </div>
                    {content.scheduledTime && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {content.scheduledTime}
                      </div>
                    )}
                    {content.assignedToName && (
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {content.assignedToName}
                      </div>
                    )}
                  </div>

                  {content.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {content.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="inline-block px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                          #{tag}
                        </span>
                      ))}
                      {content.tags.length > 3 && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">+{content.tags.length - 3} more</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1 ml-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleContentClick(content);
                  }}
                  className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all duration-200"
                  title="View Details"
                >
                  <Eye className="w-4 h-4" />
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicate(content);
                  }}
                  className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all duration-200"
                  title="Duplicate"
                >
                  <Copy className="w-4 h-4" />
                </button>

                {content.status === 'ready' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePublishNow(content);
                    }}
                    className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-all duration-200"
                    title="Publish Now"
                  >
                    <Play className="w-4 h-4" />
                  </button>
                )}

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteContent(content.id);
                  }}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>

                <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-all duration-200">
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleChecklistToggle = async (itemId: string) => {
    if (!selectedContent) return;

    const updatedChecklist = selectedContent.checklist.map(item =>
      item.id === itemId ? { ...item, completed: !item.completed } : item
    );

    try {
      // Pass the id and the updated checklist in the correct format
      await updateContent(selectedContent.id, {
        id: selectedContent.id, // Include the required id property
        checklist: updatedChecklist
      });

      // Update local state
      setSelectedContent({
        ...selectedContent,
        checklist: updatedChecklist
      });
    } catch (error) {
      console.error('Failed to update checklist:', error);
      alert('Failed to update checklist item');
    }
  };

  const renderContentDetail = () => {
    if (!selectedContent) {
      return (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No content selected</h3>
          <p className="text-gray-600 dark:text-gray-400">Click on any content item to view details</p>
        </div>
      );
    }

    const completedTasks = selectedContent.checklist.filter(item => item.completed).length;
    const totalTasks = selectedContent.checklist.length;
    const completionPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r text-white ${getPlatformColor(selectedContent.platform)}`}>
                {getPlatformIcon(selectedContent.platform)}
                <span className="ml-2 capitalize">{selectedContent.platform}</span>
              </div>
              <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${getStatusColor(selectedContent.status)}`}>
                {getStatusIcon(selectedContent.status)}
                <span className="ml-2 capitalize">{selectedContent.status}</span>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{selectedContent.title}</h2>
            <p className="text-gray-600 dark:text-gray-400">{selectedContent.description}</p>
          </div>

          <button
            onClick={() => setSelectedContent(null)}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 dark:text-white mb-3">Schedule</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Date</div>
              <div className="font-medium text-gray-900 dark:text-white">{selectedContent.scheduledDate.toLocaleDateString()}</div>
            </div>
            {selectedContent.scheduledTime && (
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Time</div>
                <div className="font-medium text-gray-900 dark:text-white">{selectedContent.scheduledTime}</div>
              </div>
            )}
          </div>
          {selectedContent.assignedToName && (
            <div className="mt-3">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Assigned to</div>
              <div className="font-medium text-gray-900 dark:text-white">{selectedContent.assignedToName}</div>
            </div>
          )}
        </div>

        {selectedContent.tags.length > 0 && (
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {selectedContent.tags.map(tag => (
                <span key={tag} className="inline-block px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {selectedContent.checklist.length > 0 && (
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Checklist</h3>
            <div className="space-y-2">
              {selectedContent.checklist.map(item => (
                <div key={item.id} className={`flex items-center gap-2 p-2 rounded ${item.completed ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
                  <input
                    type="checkbox"
                    checked={item.completed}
                    onChange={() => handleChecklistToggle(item.id)}
                    className="h-4 w-4 text-green-600 rounded focus:ring-green-500 cursor-pointer border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                  />
                  <span className={item.completed ? 'line-through text-gray-500 dark:text-gray-500' : 'text-gray-900 dark:text-gray-200'}>
                    {item.text}
                  </span>
                </div>
              ))}
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {completionPercentage}% complete ({completedTasks}/{totalTasks})
              </div>
            </div>
          </div>
        )}

        {selectedContent.notes && (
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Notes</h3>
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedContent.notes}</p>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Content Calendar</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Plan, schedule, and manage your content across all platforms</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-4 gap-4 w-full max-w-2xl">
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Content</div>
            <div className="text-xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Published</div>
            <div className="text-xl font-bold text-green-600 dark:text-green-400">{stats.byStatus.published || 0}</div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">This Week</div>
            <div className="text-xl font-bold text-primary-600 dark:text-primary-400">{stats.thisWeek}</div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Overdue</div>
            <div className="text-xl font-bold text-red-600 dark:text-red-400">{stats.overdue}</div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-white transition-colors"
              />
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl transition-colors ${showFilters ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300' : 'bg-gray-50 dark:bg-gray-700 hover:bg-white dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('month')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'month' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                <CalendarDays className="w-4 h-4" />
                Month
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'week' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                <CalendarRange className="w-4 h-4" />
                Week
              </button>
              <button
                onClick={() => setViewMode('day')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'day' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                <CalendarClock className="w-4 h-4" />
                Day
              </button>
            </div>

            <button
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl hover:from-primary-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Plus className="w-4 h-4" />
              Add Content
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Platforms</label>
                <div className="space-y-2">
                  {(['youtube', 'instagram', 'tiktok', 'twitter', 'blog'] as PlatformName[]).map(platform => (
                    <label key={platform} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.platforms.includes(platform)}
                        onChange={(e) => {
                          const newPlatforms = e.target.checked
                            ? [...filters.platforms, platform]
                            : filters.platforms.filter(p => p !== platform);
                          setFilters({ platforms: newPlatforms });
                        }}
                        className="h-4 w-4 text-primary-600 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 focus:ring-primary-500"
                      />
                      <div className="ml-2 flex items-center gap-2">
                        {getPlatformIcon(platform)}
                        <span className="text-sm capitalize">{platform}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                <div className="space-y-2">
                  {(['draft', 'ready', 'scheduled', 'published', 'overdue'] as ScheduleStatus[]).map(status => (
                    <label key={status} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.statuses.includes(status)}
                        onChange={(e) => {
                          const newStatuses = e.target.checked
                            ? [...filters.statuses, status]
                            : filters.statuses.filter(s => s !== status);
                          setFilters({ statuses: newStatuses });
                        }}
                        className="h-4 w-4 text-primary-600 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 focus:ring-primary-500"
                      />
                      <div className="ml-2 flex items-center gap-2">
                        {getStatusIcon(status)}
                        <span className="text-sm capitalize">{status}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date Range</label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => setFilters({ dateRange: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="thisWeek">This Week</option>
                  <option value="thisMonth">This Month</option>
                  <option value="nextMonth">Next Month</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {selectedItems.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <button className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  Reschedule
                </button>
                <button className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  Change Status
                </button>
                <button
                  onClick={clearSelection}
                  className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {['month', 'week', 'day'].includes(viewMode) && renderCalendarView()}

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-3">
          {!['month', 'week', 'day'].includes(viewMode) ? (
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Content List</h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {contentItems.length} item{contentItems.length !== 1 ? 's' : ''}
                </span>
              </div>
              {renderContentList()}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {selectedDate
                    ? `Content for ${selectedDate.toLocaleDateString()}`
                    : 'All Content'
                  }
                </h3>
                {selectedDate && (
                  <button
                    onClick={() => setSelectedDate(null)}
                    className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Show All
                  </button>
                )}
              </div>

              {selectedDate ? (
                <div className="space-y-3">
                  {getContentForDate(selectedDate).map(content => (
                    <div
                      key={content.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, content)}
                      onDragEnd={handleDragEnd}
                      className="p-4 border border-gray-100 dark:border-gray-700 rounded-3xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] cursor-move"
                      onClick={() => handleContentClick(content)}
                      title="Drag to reschedule"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r text-white ${getPlatformColor(content.platform)}`}>
                            {getPlatformIcon(content.platform)}
                            <span className="ml-1">{content.platform}</span>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white">{content.title}</h4>
                            {content.scheduledTime && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">{content.scheduledTime}</p>
                            )}
                          </div>
                        </div>
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(content.status)}`}>
                          {getStatusIcon(content.status)}
                          <span className="ml-1">{content.status}</span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {getContentForDate(selectedDate).length === 0 && (
                    <div className="text-center py-8">
                      <Calendar className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-600 dark:text-gray-400">No content scheduled for this date</p>
                      <button className="mt-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm">
                        Add content for {selectedDate.toLocaleDateString()}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                renderContentList()
              )}
            </div>
          )}
        </div>

        <div className="xl:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6 sticky top-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Content Details</h3>
            {renderContentDetail()}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        {Object.entries(stats.byPlatform).map(([platform, count]) => (
          <div key={platform} className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-4 text-center">
            <div className="flex justify-center mb-2">
              {getPlatformIcon(platform as PlatformName)}
            </div>
            <div className="text-lg font-bold text-gray-900 dark:text-white">{count}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 capitalize">{platform}</div>
          </div>
        ))}
      </div>

      {/* Drag feedback */}
      {dragState.isDragging && (
        <div className="fixed bottom-4 left-4 bg-primary-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 pointer-events-none">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">
              Drag to calendar to reschedule "{dragState.draggedItem?.title}"
            </span>
          </div>
        </div>
      )}

      <AddContentModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleAddContentSuccess}
      />
    </div>
  );
};

export default ContentCalendarPage;